/**
 * VideoPlayer Component
 * 
 * Displays the YouTube video player with seek functionality
 */

import React, { useRef, useEffect, useState } from 'react';
import { COLORS, SHADOWS, BORDER_RADIUS, SPACING } from '../data/constants';

const VideoPlayer = ({ videoInfo, seekToTimestamp, onTimeUpdate }) => {
  const videoId = videoInfo.id;
  const playerRef = useRef(null);
  const playerContainerId = `youtube-player-${videoId}`;
  const intervalRef = useRef(null);
  const [isApiReady, setIsApiReady] = useState(false);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  
  // Keep the ref updated
  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);
  
  // Load YouTube Iframe API
  useEffect(() => {
    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      setIsApiReady(true);
      return;
    }

    // Check if script is already being loaded
    if (window.YT && !window.YT.Player) {
      const checkInterval = setInterval(() => {
        if (window.YT && window.YT.Player) {
          setIsApiReady(true);
          clearInterval(checkInterval);
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }

    // Load the API script
    if (!document.getElementById('youtube-iframe-api')) {
      const tag = document.createElement('script');
      tag.id = 'youtube-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Set up callback
    window.onYouTubeIframeAPIReady = () => {
      console.log('YouTube API loaded');
      setIsApiReady(true);
    };
  }, []);

  // Create player when API is ready
  useEffect(() => {
    if (!isApiReady || !videoId) return;

    const initPlayer = () => {
      try {
        playerRef.current = new window.YT.Player(playerContainerId, {
          videoId: videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 0,
            modestbranding: 1,
            rel: 0
          },
          events: {
            onReady: (event) => {
              console.log('YouTube player ready');
              
              // Start tracking time updates
              if (onTimeUpdateRef.current) {
                intervalRef.current = setInterval(() => {
                  if (playerRef.current && playerRef.current.getCurrentTime) {
                    try {
                      const currentTime = playerRef.current.getCurrentTime();
                      if (onTimeUpdateRef.current) {
                        onTimeUpdateRef.current(currentTime);
                      }
                    } catch (e) {
                      console.error('Error getting current time:', e);
                    }
                  }
                }, 500);
              }
            },
            onStateChange: (event) => {
              // Clear interval when video ends or is paused
              if (event.data === window.YT.PlayerState.ENDED || event.data === window.YT.PlayerState.PAUSED) {
                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                  intervalRef.current = null;
                }
              } else if (event.data === window.YT.PlayerState.PLAYING) {
                // Restart interval when playing
                if (onTimeUpdateRef.current && !intervalRef.current) {
                  intervalRef.current = setInterval(() => {
                    if (playerRef.current && playerRef.current.getCurrentTime) {
                      try {
                        const currentTime = playerRef.current.getCurrentTime();
                        if (onTimeUpdateRef.current) {
                          onTimeUpdateRef.current(currentTime);
                        }
                      } catch (e) {
                        console.error('Error getting current time:', e);
                      }
                    }
                  }, 500);
                }
              }
            }
          }
        });
      } catch (error) {
        console.error('Error creating YouTube player:', error);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initPlayer, 100);

    return () => {
      clearTimeout(timer);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error('Error destroying player:', e);
        }
      }
    };
  }, [isApiReady, videoId, playerContainerId]);

  // Seek to timestamp when it changes
  useEffect(() => {
    if (seekToTimestamp !== null && seekToTimestamp !== undefined && playerRef.current && playerRef.current.seekTo) {
      try {
        console.log(`Seeking to ${seekToTimestamp} seconds`);
        playerRef.current.seekTo(seekToTimestamp, true);
        playerRef.current.playVideo();
      } catch (e) {
        console.error('Error seeking:', e);
      }
    }
  }, [seekToTimestamp]);

  return (
    <div style={styles.container}>
      <div style={styles.playerWrapper}>
        <div
          id={playerContainerId}
          style={styles.iframe}
        ></div>
      </div>
      <div style={styles.info}>
        <h3 style={styles.title}>{videoInfo.title}</h3>
        <p style={styles.channel}>{videoInfo.channelTitle}</p>
        <p style={styles.stats}>
          {videoInfo.statistics.viewCount.toLocaleString()} views • {videoInfo.duration.formatted}
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: BORDER_RADIUS.LARGE,
    padding: SPACING.LG,
    boxShadow: SHADOWS.RADIAL_MEDIUM,
  },
  playerWrapper: {
    position: 'relative',
    paddingBottom: '56.25%', // 16:9 aspect ratio
    height: 0,
    overflow: 'hidden',
    borderRadius: BORDER_RADIUS.MEDIUM,
    marginBottom: SPACING.MD,
  },
  iframe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: BORDER_RADIUS.MEDIUM,
  },
  info: {
    padding: `${SPACING.SM} 0`,
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: COLORS.WHITE,
    margin: `0 0 ${SPACING.SM} 0`,
    lineHeight: '1.4',
  },
  channel: {
    fontSize: '14px',
    color: COLORS.GRAY,
    margin: `0 0 ${SPACING.XS} 0`,
  },
  stats: {
    fontSize: '12px',
    color: COLORS.GRAY,
    margin: 0,
  },
};

export default VideoPlayer;

